import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity, TrendingUp, TrendingDown, Database, Clock, RefreshCw,
  Zap, DollarSign, BarChart2, CloudSun, AlertTriangle, Briefcase
} from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts'
import MetricCard from '../components/MetricCard'
import SystemHealthChart from '../components/SystemHealthChart'
import RecentSignals from '../components/RecentSignals'
import MonitoringAlerts from '../components/MonitoringAlerts'
import MarketOverview from '../components/MarketOverview'
import { api, schwab, dataSource, streaming, alerts as alertsApi } from '../lib/api'
import SystemAlerts from '../components/SystemAlerts'
import SchwabLoginCountdown from '../components/SchwabLoginCountdown'
import { useUiMode } from '../lib/uiMode'
import { fmtInt, fmtShort, timeAgo, isMarketOpen, getMarketSession } from '../lib/utils'

const SESSION_LABELS = {
  premarket: 'Pre-market',
  regular: 'Market hours: active',
  afterhours: 'After-hours',
  closed: 'Outside market hours',
}
import { useSettings } from '../context/SettingsContext'
import { useAccount } from '../context/AccountContext'

// ── Equity curve tooltip ───────────────────────────────────────────────────
const EquityTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload
  const pl = d?.pl ?? 0
  return (
    <div className="rounded-lg px-3 py-2 text-xs border"
      style={{ background: '#0d1423', borderColor: '#1f2937' }}>
      <p className="text-slate-500 mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {new Date(label * 1000).toLocaleDateString()}
      </p>
      <p className="text-slate-200 font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        ${Number(payload[0]?.value).toLocaleString('en-US', { maximumFractionDigits: 0 })}
      </p>
      <p className={pl >= 0 ? 'text-emerald-400' : 'text-rose-400'}
        style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {pl >= 0 ? '+' : ''}${pl.toLocaleString('en-US', { maximumFractionDigits: 0 })}
      </p>
    </div>
  )
}

// ── Market weather widget ──────────────────────────────────────────────────
function MarketWeatherPanel({ regime }) {
  if (!regime || !regime.length) return null

  const total = regime.reduce((s, r) => s + (r.symbol_count || 0), 0)
  const dominant = regime[0]
  if (!dominant) return null

  const regimeColor = {
    BULL:     { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)', text: '#10b981', label: 'BULLISH' },
    BEAR:     { bg: 'rgba(244,63,94,0.08)',  border: 'rgba(244,63,94,0.2)',  text: '#f43f5e', label: 'BEARISH' },
    SIDEWAYS: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', text: '#f59e0b', label: 'SIDEWAYS' },
  }
  const palette = regimeColor[dominant.market_regime] || regimeColor.SIDEWAYS

  return (
    <div className="rounded-xl border p-5" style={{ background: palette.bg, borderColor: palette.border }}>
      <h3 className="section-header" style={{ color: palette.text }}>
        <CloudSun className="h-3.5 w-3.5" style={{ marginLeft: '-2px' }} />
        Market Weather
      </h3>
      <div className="flex items-end justify-between mb-4">
        <p className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '1.6rem', color: palette.text }}>
          {palette.label}
        </p>
        <p className="text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
          {total} symbols scanned
        </p>
      </div>
      <div className="space-y-2">
        {regime.map(r => {
          const pct = total ? Math.round((r.symbol_count / total) * 100) : 0
          const c = regimeColor[r.market_regime]?.text || '#64748b'
          return (
            <div key={r.market_regime} className="flex items-center gap-3">
              <span className="w-16 text-right tabular-nums"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: c }}>
                {r.market_regime}
              </span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#1e293b' }}>
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, background: c }} />
              </div>
              <span className="w-8 text-right tabular-nums text-slate-500"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
                {pct}%
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function Dashboard() {
  const classic = useUiMode() === 'orig'   // 2026-10-04: ORIG restores the KPI rows that were live before the redesign
  const { settings, passesFilter } = useSettings()
  const { account, tier, bpRatioPct, minBuyingPowerPct, refresh: refreshAccount, selectedAccount } = useAccount()
  const [goalAccount, setGoalAccount] = useState(null)
  const [health,    setHealth]    = useState(null)
  const [dbMetrics, setDbMetrics] = useState(null)
  const [signals,   setSignals]   = useState([])
  const [monitoringAlerts, setMonitoringAlerts] = useState([])
  const [monitoringLoading, setMonitoringLoading] = useState(true)
  const [systemAlerts, setSystemAlerts] = useState([])
  const [recentResolved, setRecentResolved] = useState([])   // T-336: alerts resolved in the last 24 h (RESOLVED strip)
  const [mainPos, setMainPos] = useState(null)   // MAIN positions + stop status (2026-10-04)
  const [market,    setMarket]    = useState({ chartData: [], stats: {} })
  const [equity,    setEquity]    = useState([])
  const [equityPeriod, setEquityPeriod] = useState('1M')
  const [regime,      setRegime]      = useState([])
  const [schwabStatus,  setSchwabStatus]  = useState(null)
  const [activeSource,  setActiveSource]  = useState(null)
  const [sourceSwitching, setSourceSwitching] = useState(false)
  const [wsStatus,      setWsStatus]      = useState(null)
  const [livePnl,       setLivePnl]       = useState(null)
  const [loading,     setLoading]     = useState(true)
  const [lastSync,    setLastSync]    = useState(null)
  const [error,       setError]       = useState(null)
  const [perf,          setPerf]          = useState(null)
  const [perfLoading,   setPerfLoading]   = useState(false)
  const [goalCheckedAt, setGoalCheckedAt] = useState(null)
  const [posFilter,     setPosFilter]     = useState('')
  const [posSort,       setPosSort]       = useState({ key: 'minutes_to_timebox', dir: 'asc' })
  const [recentActivity, setRecentActivity] = useState([])

  // Core fetch — only the 4 fast endpoints that drive System Status and main KPIs.
  // api.account() and api.marketRegime() are intentionally excluded here because:
  //   account() requires an Alpaca API roundtrip (~1-3s) that would block health data
  //   marketRegime() runs COUNT(DISTINCT) on a large hypertable and can be slow
  // Both are fetched in their own effects below.
  const fetchAll = useCallback(async () => {
    try {
      const [h, db, sig, mkt] = await Promise.allSettled([
        api.health(),
        api.dbMetrics(),
        api.signalsRecent(),
        api.marketOverview(),
      ])
      if (h.status   === 'fulfilled') setHealth(h.value)
      if (db.status  === 'fulfilled') setDbMetrics(db.value)
      if (sig.status === 'fulfilled') {
        const filtered = (sig.value.signals || []).filter(passesFilter)
        setSignals(filtered)
      }
      if (mkt.status === 'fulfilled') setMarket({ chartData: mkt.value.chart_data || [], stats: mkt.value.stats || {} })
      setLastSync(new Date())
      setError(null)
    } catch (e) {
      setError('Unable to reach the engine. Check that the Linux engine and the relay tunnel are running.')
    } finally {
      setLoading(false)
    }
  }, [passesFilter])

  // Regime fetch — separate because COUNT(DISTINCT) on large hypertable can be slow
  const fetchRegime = useCallback(async () => {
    try {
      const res = await api.marketRegime()
      if (res.success) {
        const rows = (res.data || [])
          .map(r => ({ market_regime: r[0], symbol_count: Number(r[1]) }))
          .filter(r => r.market_regime && r.market_regime !== 'NaN')
          .sort((a, b) => b.symbol_count - a.symbol_count)
        setRegime(rows)
      }
    } catch { /* regime is optional */ }
  }, [])

  // Goal-progress check — on demand (button) + once on mount. Reuses the existing
  // account/performance endpoints rather than a new backend route: /api/account
  // already has equity, /api/performance already has the exit-reason breakdown.
  // This is a live numbers refresh, not the deeper analytical check-in (pattern-
  // finding, hypotheses) — that runs daily via the scheduled cloud routine, or on
  // request from Claude directly. See docs/GOAL_RECOVER_TO_100K.md.
  const checkGoalNow = useCallback(async () => {
    setPerfLoading(true)
    try {
      const [, perfRes] = await Promise.allSettled([refreshAccount(), api.performance(7)])
      if (perfRes.status === 'fulfilled') setPerf(perfRes.value)
      setGoalCheckedAt(new Date())
    } finally {
      setPerfLoading(false)
    }
  }, [refreshAccount])

  // Tier 2 open positions — separate, polls every 30s during market hours.
  // 2026-09-09: was api.livePnl() (momentum's own Phase A/B trailing-stop
  // math, an account that hasn't held a real position since trading_enabled
  // went false). Swapped to the real live account -- same tier2Positions()
  // endpoint Portfolio.jsx uses, reshaped into the {positions, count,
  // total_unrealized_pl} shape this panel already expects so the render
  // logic below barely has to change.
  const fetchLivePnl = useCallback(async () => {
    try {
      const res = await api.tier2Positions()
      if (res.success) {
        const total_unrealized_pl = (res.positions || []).reduce((s, p) => s + (p.pnl_dollar || 0), 0)
        setLivePnl({ positions: res.positions, count: res.count, total_unrealized_pl })
      }
    } catch { /* optional */ }
  }, [])

  // Recent Tier 2 activity — condensed snapshot for the dashboard, mirroring
  // Alpaca's own "Recent Orders" panel on its account home screen. Full
  // history lives on /trades; this is just the last few real fills.
  const fetchRecentActivity = useCallback(async () => {
    try {
      const res = await api.tier2Activity(1, 8)
      if (res.success) setRecentActivity(res.activity || [])
    } catch { /* optional */ }
  }, [])

  // Monitoring alerts — separate, low-frequency (these are review items, not live
  // KPIs), poll every 5 min rather than the 30-60s cadence used elsewhere.
  const fetchMonitoringAlerts = useCallback(async () => {
    try {
      const res = await api.monitoringAlerts(24, 20)
      setMonitoringAlerts(res.alerts || [])
    } catch { /* optional */ }
    finally {
      setMonitoringLoading(false)
    }
  }, [])

  // System alerts (system_alerts table, written by the monitoring cloud
  // routines) — poll every 30s so a critical alert shows up fast.
  const fetchSystemAlerts = useCallback(async () => {
    try {
      const res = await alertsApi.active()
      setSystemAlerts(res.alerts || [])
    } catch { /* non-fatal */ }
  }, [])

  // MAIN account positions and their stops: the one book the dashboard never showed (GUI audit 2026-10-04).
  // Polls every 30 s with the other dashboard timers, but only while the tab is visible.
  const fetchMainPos = useCallback(async () => {
    try {
      const res = await api.mainPositions()
      if (res.success) setMainPos(res.summary)
    } catch { /* optional; the card shows a dash */ }
  }, [])
  useEffect(() => {
    fetchMainPos()
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchMainPos() }, 30_000)
    return () => clearInterval(id)
  }, [fetchMainPos])

  // ACK a whole alert type (every unresolved row in the group) in one click.
  // T-336 (owner 2026-11-02): CLEAR ALL for one alert type / everything. The server resolves every unresolved row of the type, not only the 50 shown.
  const fetchRecentResolved = useCallback(async () => {
    try { const r = await alertsApi.recentResolved(); setRecentResolved(r?.alerts || []) } catch { /* noqa: swallow - the strip is optional */ }
  }, [])
  const clearAlertType = useCallback(async (source, type, ids) => {
    const gone = new Set(ids || [])
    setSystemAlerts(prev => prev.filter(a => !gone.has(a.id)))  // optimistic
    try { await alertsApi.resolveType(source, type) } finally { fetchSystemAlerts(); fetchRecentResolved() }
  }, [fetchSystemAlerts, fetchRecentResolved])
  const clearAllAlerts = useCallback(async () => {
    setSystemAlerts([])
    try { await alertsApi.resolveAll() } finally { fetchSystemAlerts(); fetchRecentResolved() }
  }, [fetchSystemAlerts, fetchRecentResolved])

  const ackAlertGroup = useCallback(async (ids) => {
    const gone = new Set(ids)
    setSystemAlerts(prev => prev.filter(a => !gone.has(a.id)))  // optimistic
    const results = await Promise.allSettled(ids.map(id => alertsApi.resolve(id)))
    if (results.some(r => r.status === 'rejected')) fetchSystemAlerts()  // reconcile on failure
  }, [fetchSystemAlerts])

  // Equity curve fetch — separate, period-dependent. Follows the header's
  // account switcher, same as the KPI tiles above (this is a general "recent
  // equity trend" chart, not the fixed-target Path-to-$100K card below).
  const fetchEquity = useCallback(async (period) => {
    try {
      const res = await api.portfolioHistory(period, selectedAccount)
      if (res.success && res.data?.length) {
        setEquity(res.data.filter(d => d.equity != null && d.equity > 0))
      }
    } catch { /* equity curve is optional */ }
  }, [selectedAccount])

  // Path-to-$100K goal fetch — ALWAYS the MAIN account, deliberately independent
  // of the header's account switcher. See docs/GOAL_RECOVER_TO_100K.md: the
  // $100K target was set against MAIN specifically (Tier 2 started at $50K, a
  // different basis) — this card must not silently follow the switcher the way
  // it silently followed a hardcoded 'tier2' default for over a week before
  // this was caught (2026-09-19).
  const fetchGoalAccount = useCallback(async () => {
    try {
      const acct = await api.account('main')
      if (acct.success) setGoalAccount(acct)
    } catch { /* non-fatal, card shows — for missing data */ }
  }, [])

  useEffect(() => {
    fetchGoalAccount()
    const id = setInterval(fetchGoalAccount, 30_000)
    return () => clearInterval(id)
  }, [fetchGoalAccount])

  useEffect(() => {
    fetchAll()
    const id = setInterval(fetchAll, settings.refreshInterval * 1000)
    return () => clearInterval(id)
  }, [fetchAll, settings.refreshInterval])

  // Load regime once on mount, then every 60s (account data comes from AccountContext)
  useEffect(() => {
    fetchRegime()
    const id = setInterval(fetchRegime, 60_000)
    return () => clearInterval(id)
  }, [fetchRegime])

  // Live P&L — poll every 30s (Alpaca call, keep interval moderate)
  useEffect(() => {
    fetchLivePnl()
    const id = setInterval(fetchLivePnl, 30_000)
    return () => clearInterval(id)
  }, [fetchLivePnl])

  // Recent activity — poll every 30s, same cadence as Live P&L
  useEffect(() => {
    fetchRecentActivity()
    const id = setInterval(fetchRecentActivity, 30_000)
    return () => clearInterval(id)
  }, [fetchRecentActivity])

  // Monitoring alerts — poll every 5 min, these are review items not live KPIs
  useEffect(() => {
    fetchMonitoringAlerts()
    const id = setInterval(fetchMonitoringAlerts, 5 * 60_000)
    return () => clearInterval(id)
  }, [fetchMonitoringAlerts])

  // System alerts (from the monitoring cloud routines) — poll every 30s
  useEffect(() => {
    fetchSystemAlerts()
    fetchRecentResolved()
    const id = setInterval(() => { fetchSystemAlerts(); fetchRecentResolved() }, 30_000)
    return () => clearInterval(id)
  }, [fetchSystemAlerts, fetchRecentResolved])

  // Data source + Schwab status — poll every 30s
  useEffect(() => {
    const fetchSource = () => {
      schwab.status().then(d => setSchwabStatus(d)).catch(() => {})
      dataSource.get().then(d => { if (d.success) setActiveSource(d.active_source) }).catch(() => {})
    }
    fetchSource()
    const id = setInterval(fetchSource, 30_000)
    return () => clearInterval(id)
  }, [])

  // WebSocket health — poll every 30s (no auth required)
  useEffect(() => {
    const fetchWsStatus = () => {
      streaming.status().then(d => setWsStatus(d)).catch(() => {})
    }
    fetchWsStatus()
    const id = setInterval(fetchWsStatus, 30_000)
    return () => clearInterval(id)
  }, [])

  const handleSourceSwitch = async (target) => {
    if (sourceSwitching || activeSource === target) return
    setSourceSwitching(true)
    try {
      const res = await dataSource.set(target)
      if (res.success) setActiveSource(res.active_source)
    } catch { /* ignore */ }
    setSourceSwitching(false)
  }

  useEffect(() => { fetchEquity(equityPeriod) }, [fetchEquity, equityPeriod])

  // Goal-progress: once on mount only — this is an on-demand panel (button-driven),
  // not part of the auto-refresh cadence.
  useEffect(() => { checkGoalNow() }, [checkGoalNow])

  // Derived values
  const totalRecords = dbMetrics?.database_records
  const latestTs     = dbMetrics?.latest_timestamp || health?.latest_data
  const recentAct    = dbMetrics?.recent_activity_24h
  const signalCount  = signals.length
  const buys         = signals.filter(s => (s.direction||'').toLowerCase() === 'buy').length
  const sells        = signalCount - buys

  // Equity chart formatting
  const equityMin = equity.length ? Math.min(...equity.map(d => d.equity)) * 0.995 : 0
  const equityMax = equity.length ? Math.max(...equity.map(d => d.equity)) * 1.005 : 0
  const equityPositive = equity.length >= 2
    ? equity[equity.length - 1].equity >= equity[0].equity : true

  // Goal-progress derived values ($100K recovery target — docs/GOAL_RECOVER_TO_100K.md)
  // Always MAIN's numbers (goalAccount, fetched independently above) — never
  // the switchable `account`, regardless of what's selected in the header.
  const GOAL_TARGET = 100000
  const goalEquity = goalAccount?.equity ?? goalAccount?.portfolio_value ?? null
  const goalGap = goalEquity != null ? GOAL_TARGET - goalEquity : null
  const goalPct = goalEquity != null ? Math.min(100, Math.max(0, (goalEquity / GOAL_TARGET) * 100)) : null

  return (
    <div className="space-y-5">

      {/* Consolidated system alerts — all critical/warning conditions in one panel */}
      <SystemAlerts health={health} schwabStatus={schwabStatus} wsStatus={wsStatus}
        dbAlerts={systemAlerts} onAckGroup={ackAlertGroup} onClearType={clearAlertType} onClearAll={clearAllAlerts} recentResolved={recentResolved} mainSummary={mainPos} />

      {/* Next required Schwab browser login (7 days after the last full login; T-043) */}
      <SchwabLoginCountdown deadline={schwabStatus?.refresh_token_reauth_deadline} tokenStored={!!schwabStatus?.token_stored} checked={!!schwabStatus} />

      {/* Path to $100K — top-level goal, see docs/GOAL_RECOVER_TO_100K.md */}
      <div className="rounded-lg p-4"
        style={{ background: 'var(--bg-surface, rgba(255,255,255,0.03))', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-slate-500 uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
              Path to $100K <span style={{ opacity: 0.6 }}>· MAIN account, always — not the account switcher above</span>
            </p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-semibold text-slate-100">
                {goalEquity != null ? `$${goalEquity.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
              </span>
              {goalGap != null && (
                <span className={goalGap <= 0 ? 'text-emerald-400' : 'text-slate-500'} style={{ fontSize: '0.8rem' }}>
                  {goalGap <= 0
                    ? `+$${Math.abs(goalGap).toLocaleString('en-US', { maximumFractionDigits: 0 })} above target`
                    : `$${goalGap.toLocaleString('en-US', { maximumFractionDigits: 0 })} to go (${(100 - goalPct).toFixed(1)}%)`}
                </span>
              )}
            </div>
          </div>
          <button onClick={checkGoalNow} disabled={perfLoading}
            className="flex items-center gap-1.5 text-slate-600 hover:text-slate-300 border border-gray-800 rounded-lg px-3 py-1.5 hover:bg-gray-800 transition-colors disabled:opacity-50"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em' }}>
            <RefreshCw className={`h-3 w-3 ${perfLoading ? 'animate-spin' : ''}`} />
            {perfLoading ? 'CHECKING…' : 'CHECK NOW'}
          </button>
        </div>

        {goalPct != null && (
          <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full transition-all"
              style={{ width: `${goalPct}%`, background: goalPct >= 100 ? '#34d399' : '#60a5fa' }} />
          </div>
        )}

        {perf && (
          <p className="mt-3 text-slate-500" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
            Last 7d: {perf.summary?.total_trades ?? 0} trades · {(perf.summary?.win_rate_pct ?? 0).toFixed(0)}% win rate
            · avg {(perf.summary?.avg_pnl_pct ?? 0) >= 0 ? '+' : ''}{(perf.summary?.avg_pnl_pct ?? 0).toFixed(2)}%
            {goalCheckedAt && ` · checked ${timeAgo(goalCheckedAt)}`}
            {' · all exits, not filtered for reconciliation artifacts (see Data page); not decision-grade'}
          </p>
        )}
        <p className="mt-1 text-slate-700" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.63rem' }}>
          Daily automated check-in posts to the Monitoring Alerts feed (source: goal-checkin); narrative history in docs/GOAL_RECOVER_TO_100K.md.
        </p>
      </div>

      {/* Backend unreachable */}
      {error && (
        <div className="rounded-lg px-4 py-3 text-sm"
          style={{ background: 'rgba(244,63,94,0.07)', border: '1px solid rgba(244,63,94,0.2)', color: '#fb7185' }}>
          {error}
        </div>
      )}

      {/* Page header */}
      <div className="flex items-center justify-between">
        <p className="text-slate-700"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.06em' }}>
          {lastSync ? `SYNC ${timeAgo(lastSync)}` : 'LOADING…'}
          {` · AUTO ${settings.refreshInterval}s`}
        </p>
        <button onClick={fetchAll}
          className="flex items-center gap-1.5 text-slate-600 hover:text-slate-300 border border-gray-800 rounded-lg px-3 py-1.5 hover:bg-gray-800 transition-colors"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em' }}>
          <RefreshCw className="h-3 w-3" />
          REFRESH
        </button>
      </div>

      {classic ? (
        <>
      {/* Account KPI row */}
      {account && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              label: 'Portfolio Value',
              value: `$${account.portfolio_value?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
              icon: <DollarSign className="h-5 w-5" />, color: 'blue',
            },
            {
              label: 'Buying Power',
              value: `$${account.buying_power?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
              icon: <BarChart2 className="h-5 w-5" />, color: 'purple',
              subtitle: `Cash $${account.cash?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
            },
            {
              label: 'Intraday P&L',
              value: `${account.intraday_pl >= 0 ? '+' : ''}$${Math.abs(account.intraday_pl)?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
              icon: account.intraday_pl >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />,
              color: account.intraday_pl >= 0 ? 'green' : 'red',
              subtitle: `${account.intraday_pl_pct >= 0 ? '+' : ''}${account.intraday_pl_pct?.toFixed(2)}% vs yesterday`,
            },
            {
              // PDT accounts have no day trade limit — show count without /3 cap.
              // Non-PDT accounts are limited to 3 round-trips per rolling 5 days.
              label: 'Day Trades',
              value: account.pattern_day_trader
                ? `${account.daytrade_count}`
                : `${account.daytrade_count}/3`,
              icon: <AlertTriangle className="h-5 w-5" />,
              color: (!account.pattern_day_trader && account.daytrade_count >= 3) ? 'red'
                   : (!account.pattern_day_trader && account.daytrade_count >= 2) ? 'amber'
                   : 'green',
              subtitle: account.pattern_day_trader ? 'PDT — no limit' : 'Non-PDT · limit 3',
            },
          ].map(({ label, value, icon, color, subtitle }) => (
            <MetricCard key={label} title={label} value={value} icon={icon}
              color={color} subtitle={subtitle} loading={false} />
          ))}
        </div>
      )}

      {/* System KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard title="Signals (24h)" value={loading ? null : signalCount}
          icon={<TrendingUp className="h-5 w-5" />} color="blue"
          subtitle={signalCount ? `${buys} buy · ${sells} sell` : undefined}
          trend={signalCount > 0 ? 'up' : 'stable'} loading={loading} />
        <MetricCard title="DB Records" value={loading ? null : fmtShort(totalRecords)}
          icon={<Database className="h-5 w-5" />} color="purple"
          subtitle={recentAct != null ? `${fmtShort(recentAct)} bars/24h` : undefined}
          loading={loading} />
        <MetricCard title="Data Freshness"
          value={loading ? null : (latestTs ? timeAgo(latestTs) : 'No data')}
          icon={<Clock className="h-5 w-5" />}
          color={latestTs && (Date.now() - new Date(latestTs)) < 300_000 ? 'green' : 'amber'}
          subtitle="Latest bar timestamp" loading={loading} />
        <MetricCard title="System Status"
          value={loading ? null : (health?.status === 'healthy' ? 'Healthy' : 'Degraded')}
          icon={<Activity className="h-5 w-5" />}
          color={health?.status === 'healthy' ? 'green' : 'red'}
          subtitle={SESSION_LABELS[health?.market_session ?? getMarketSession()]}
          loading={loading} />
      </div>
        </>
      ) : (
        <>
      {/* KPI row (2026-10-04): portfolio value and intraday P&L were repeated here and in the header, and DB Records
          had no decision attached, so they are gone; MAIN positions with stop status is new. */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {account && (
          <MetricCard title="Buying Power"
            value={`$${account.buying_power?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            icon={<BarChart2 className="h-5 w-5" />} color="purple"
            subtitle={`Cash $${account.cash?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`} loading={false} />
        )}
        {account && (
          <MetricCard title="Day Trades"
            value={account.pattern_day_trader ? `${account.daytrade_count}` : `${account.daytrade_count}/3`}
            icon={<AlertTriangle className="h-5 w-5" />}
            color={(!account.pattern_day_trader && account.daytrade_count >= 3) ? 'red'
                 : (!account.pattern_day_trader && account.daytrade_count >= 2) ? 'amber' : 'green'}
            subtitle={account.pattern_day_trader ? 'PDT — no limit' : 'Non-PDT · limit 3'} loading={false} />
        )}
        <Link to="/positions" title="MAIN positions and stop status">
          <MetricCard title="MAIN Positions" value={mainPos ? mainPos.count : null}
            icon={<Briefcase className="h-5 w-5" />}
            color={!mainPos ? 'slate' : mainPos.unprotected_count > 0 ? 'red' : 'green'}
            subtitle={!mainPos ? undefined : mainPos.unprotected_count > 0
              ? `${mainPos.unprotected_count} NO STOP: ${mainPos.unprotected.slice(0, 3).join(', ')}${mainPos.unprotected_count > 3 ? '…' : ''}`
              : 'all protected'}
            loading={!mainPos} />
        </Link>
        <MetricCard title="Signals (24h)" value={loading ? null : signalCount}
          icon={<TrendingUp className="h-5 w-5" />} color="blue"
          subtitle={signalCount ? `${buys} buy · ${sells} sell` : undefined}
          trend={signalCount > 0 ? 'up' : 'stable'} loading={loading} />
        <MetricCard title="Data Freshness"
          value={loading ? null : (latestTs ? timeAgo(latestTs) : 'No data')}
          icon={<Clock className="h-5 w-5" />}
          color={latestTs && (Date.now() - new Date(latestTs)) < 300_000 ? 'green' : 'amber'}
          subtitle="Latest bar timestamp" loading={loading} />
        <MetricCard title="System Status"
          value={loading ? null : (health?.status === 'healthy' ? 'Healthy' : 'Degraded')}
          icon={<Activity className="h-5 w-5" />}
          color={health?.status === 'healthy' ? 'green' : 'red'}
          subtitle={SESSION_LABELS[health?.market_session ?? getMarketSession()]}
          loading={loading} />
      </div>
        </>
      )}

      {/* Equity curve */}
      {equity.length > 0 && (
        <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="section-header" style={{ marginBottom: 0 }}>Portfolio Equity Curve</h3>
            <div className="flex gap-1.5">
              {['1D','1W','1M'].map(p => (
                <button key={p} onClick={() => setEquityPeriod(p)}
                  className="px-2.5 py-1 rounded text-xs font-medium transition-all"
                  style={{
                    fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem',
                    background: equityPeriod === p ? '#1d4ed8' : '#0f172a',
                    color:      equityPeriod === p ? '#bfdbfe' : '#64748b',
                    border:     equityPeriod === p ? '1px solid rgba(59,130,246,0.4)' : '1px solid #374151',
                  }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={equity} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={equityPositive ? '#10b981' : '#f43f5e'} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={equityPositive ? '#10b981' : '#f43f5e'} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 5" stroke="#1e293b" />
                <XAxis dataKey="time"
                  tickFormatter={ts => new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  tick={{ fontSize: 9, fill: '#475569', fontFamily: 'JetBrains Mono, monospace' }}
                  stroke="#1e293b" interval="preserveStartEnd" />
                <YAxis domain={[equityMin, equityMax]}
                  tickFormatter={v => `$${(v/1000).toFixed(0)}k`}
                  tick={{ fontSize: 9, fill: '#475569', fontFamily: 'JetBrains Mono, monospace' }}
                  stroke="#1e293b" />
                <Tooltip content={<EquityTooltip />} />
                <Area type="monotone" dataKey="equity"
                  stroke={equityPositive ? '#10b981' : '#f43f5e'}
                  fill="url(#eqGrad)" strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tier 2 open positions — 2026-09-09: was momentum's Phase A/B stop
          math (PHASE/STOP/DIST TO STOP), an account with no real position
          since trading_enabled went false. Now the real live account, with
          the two proximities that actually govern a Tier 2 exit -- see
          Portfolio.jsx's Open Positions panel for the same shape. */}
      {livePnl?.positions?.length > 0 && (() => {
        const COLS = [
          { key: 'symbol',             label: 'SYMBOL',      align: 'left'  },
          { key: 'entry_price',        label: 'ENTRY',       align: 'right' },
          { key: 'current_price',      label: 'CURRENT',     align: 'right' },
          { key: 'pnl_pct',            label: 'P&L',         align: 'right' },
          { key: 'signal_distance',    label: 'SIG. DIST',   align: 'right' },
          { key: 'minutes_to_timebox', label: 'TO TIMEBOX',  align: 'right' },
          { key: 'minutes_held',       label: 'HELD',        align: 'right' },
        ]
        const q = posFilter.trim().toUpperCase()
        const rows = livePnl.positions
          .filter(p => !q || p.symbol.toUpperCase().includes(q))
          .slice()
          .sort((a, b) => {
            // nulls (no timebox / no oscillator data) sort last regardless of direction
            let av = a[posSort.key], bv = b[posSort.key]
            if (av == null && bv == null) return 0
            if (av == null) return 1
            if (bv == null) return -1
            const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
            return posSort.dir === 'asc' ? cmp : -cmp
          })
        const toggleSort = (key) => setPosSort(prev =>
          prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
        )
        return (
        <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="section-header" style={{ marginBottom: 0 }}>
              <DollarSign className="h-3.5 w-3.5" style={{ marginLeft: '-2px' }} />
              Open Positions — Tier 2 Live P&L
            </h3>
            <div className="flex items-center gap-4">
              <span className="tabular-nums"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem',
                         color: livePnl.total_unrealized_pl >= 0 ? '#10b981' : '#f43f5e', fontWeight: 700 }}>
                {livePnl.total_unrealized_pl >= 0 ? '+' : ''}${livePnl.total_unrealized_pl.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                {' '}unrealized
              </span>
              <span className="text-slate-600 tabular-nums"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
                {rows.length}{rows.length !== livePnl.count ? ` / ${livePnl.count}` : ''} positions
              </span>
              <input
                value={posFilter}
                onChange={e => setPosFilter(e.target.value)}
                placeholder="Filter symbol…"
                className="rounded-lg px-2.5 py-1 text-slate-300 placeholder-slate-600"
                style={{ background: '#0f172a', border: '1px solid #1e293b', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', width: '120px' }}
              />
            </div>
          </div>
          <div className="overflow-auto" style={{ maxHeight: '420px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1e293b' }}>
                  {COLS.map(c => (
                    <th key={c.key} onClick={() => toggleSort(c.key)}
                      style={{ padding: '4px 8px', textAlign: c.align, color: posSort.key === c.key ? '#94a3b8' : '#475569',
                               fontWeight: 600, letterSpacing: '0.07em', fontSize: '0.6rem', whiteSpace: 'nowrap',
                               cursor: 'pointer', userSelect: 'none', position: 'sticky', top: 0, background: '#111827' }}>
                      {c.label}{posSort.key === c.key ? (posSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(p => {
                  const pnlColor   = p.pnl_pct >= 0 ? '#10b981' : '#f43f5e'
                  const sigColor   = p.signal_distance == null ? '#475569'
                                   : p.signal_distance <= 0 ? '#10b981' : p.signal_distance <= 5 ? '#f59e0b'
                                   : '#64748b'
                  const tbox       = p.minutes_to_timebox
                  const tboxColor  = tbox == null ? '#475569'
                                   : tbox < 0 ? '#f43f5e' : tbox < 5 ? '#f43f5e' : tbox < 15 ? '#f59e0b'
                                   : '#64748b'
                  const heldHrs    = p.minutes_held != null ? (p.minutes_held / 60).toFixed(1) : '—'
                  return (
                    <tr key={p.symbol} style={{ borderBottom: '1px solid rgba(30,41,59,0.4)' }}
                      className="hover:bg-slate-900 transition-colors">
                      <td style={{ padding: '5px 8px', color: '#e2e8f0', fontWeight: 700 }}>{p.symbol}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: '#64748b' }}>
                        ${p.entry_price.toFixed(2)}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: '#94a3b8' }}>
                        {p.current_price != null ? `$${p.current_price.toFixed(2)}` : '—'}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: pnlColor, fontWeight: 600 }}>
                        {p.pnl_pct >= 0 ? '+' : ''}{p.pnl_pct.toFixed(2)}%
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: sigColor, fontWeight: sigColor !== '#64748b' ? 700 : 400 }}>
                        {p.signal_distance == null ? '—' : p.signal_distance <= 0 ? 'past' : p.signal_distance.toFixed(1)}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: tboxColor, fontWeight: tboxColor !== '#64748b' ? 700 : 400 }}>
                        {tbox == null ? 'none' : tbox < 0 ? 'OVERDUE' : `${tbox.toFixed(0)}m`}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: '#475569' }}>
                        {heldHrs}h
                      </td>
                    </tr>
                  )
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={COLS.length} style={{ padding: '16px 8px', textAlign: 'center', color: '#475569' }}>
                    No positions match "{posFilter}"
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )
      })()}

      {/* Recent Activity — condensed snapshot of real Tier 2 fills, mirroring
          Alpaca's own "Recent Orders" panel on its account home screen. */}
      {recentActivity.length > 0 && (
        <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="section-header" style={{ marginBottom: 0 }}>
              <Zap className="h-3.5 w-3.5 text-amber-500" style={{ marginLeft: '-2px' }} />
              Recent Activity
            </h3>
            <Link to="/trades" className="text-cyan-400 hover:text-cyan-300 transition-colors"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', letterSpacing: '0.06em' }}>
              VIEW ALL →
            </Link>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e293b' }}>
                {['TIME', 'SYMBOL', 'SIDE', 'PRICE', 'P&L', 'REASON'].map((h, i) => (
                  <th key={h} style={{ padding: '4px 8px', textAlign: i >= 3 && i <= 4 ? 'right' : 'left',
                                        color: '#475569', fontWeight: 600, letterSpacing: '0.07em', fontSize: '0.6rem' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentActivity.map((a, i) => {
                const buy = a.side === 'BUY'
                const pnlColor = a.pnl_pct == null ? '#64748b' : a.pnl_pct >= 0 ? '#10b981' : '#f43f5e'
                return (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(30,41,59,0.4)' }} className="hover:bg-slate-900 transition-colors">
                    <td style={{ padding: '5px 8px', color: '#64748b', whiteSpace: 'nowrap' }}>{timeAgo(a.time)}</td>
                    <td style={{ padding: '5px 8px', color: '#e2e8f0', fontWeight: 700 }}>{a.symbol}</td>
                    <td style={{ padding: '5px 8px', color: buy ? '#60a5fa' : '#94a3b8', fontWeight: 600 }}>{a.side}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', color: '#94a3b8' }}>
                      {a.price != null ? `$${a.price.toFixed(2)}` : '—'}
                    </td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', color: pnlColor, fontWeight: 600 }}>
                      {a.pnl_pct != null ? `${a.pnl_pct >= 0 ? '+' : ''}${a.pnl_pct.toFixed(2)}%` : '—'}
                    </td>
                    <td style={{ padding: '5px 8px', color: '#475569' }}>{a.reason || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <MarketOverview chartData={market.chartData} stats={market.stats} loading={loading} />
        <RecentSignals signals={signals.slice(0, 8)} loading={loading} />
      </div>

      {/* Monitoring alerts — bullish signals on the 102 tracked-not-traded symbols */}
      <MonitoringAlerts
        alerts={monitoringAlerts}
        loading={monitoringLoading}
        onEnabled={(symbol) =>
          setMonitoringAlerts(prev => prev.filter(a => a.symbol !== symbol))
        }
      />

      {/* System health + Market Weather + Trading engine */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <SystemHealthChart health={health} dbMetrics={dbMetrics} loading={loading} />

        <MarketWeatherPanel regime={regime} />

        {/* Trading engine */}
        <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
          <h3 className="section-header">
            <Zap className="h-3.5 w-3.5 text-amber-500" style={{ marginLeft: '-2px' }} />
            Trading Engine
          </h3>
          <div className="space-y-0">
            {[
              ['Architecture',   `${health?.running_code_version || '—'} · Rotating Collection`],
              ['Collection',     `Every 1 min · ${health?.rotation_groups ?? '—'} groups`],
              ['Signal Scan',    `Every ${health?.rotation_groups ?? '—'} min`],
              // Structural constants from sf_signal_scanner.py (INITIAL_STOP_PERCENT,
              // TRAILING_STOP_PERCENT, DISASTER_STOP_LOSS_PERCENT, ATR trail clamp) —
              // matches the per-position math already shown in the Live P&L table above.
              ['Stops',          '2.5% fixed → ATR 2.5–8% trail · 6% disaster'],
              ['Mode',           'Paper (Alpaca Paper)'],
              ['Symbols',        `${dbMetrics?.active_watchlist_symbols ?? '—'} watchlist`],
              ['Indicators',     'RSI · SuperTrend · MACD +60'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between items-start gap-3 py-2"
                style={{ borderBottom: '1px solid rgba(30,41,59,0.5)' }}>
                <span className="text-slate-600 flex-shrink-0 uppercase"
                  style={{ fontSize: '0.6rem', letterSpacing: '0.1em', fontWeight: 600 }}>
                  {k}
                </span>
                <span className="text-slate-400 text-right"
                  style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
                  {v}
                </span>
              </div>
            ))}

            {/* Data source toggle */}
            <div className="pt-3 mt-1" style={{ borderTop: '1px solid rgba(30,41,59,0.8)' }}>
              <p className="text-slate-600 uppercase mb-2"
                style={{ fontSize: '0.6rem', letterSpacing: '0.1em', fontWeight: 600 }}>
                Data Feed
              </p>
              <div className="flex gap-1.5">
                {['schwab', 'alpaca'].map(src => {
                  const isActive   = activeSource === src
                  const isSchwab   = src === 'schwab'
                  const schwabOk   = schwabStatus?.token_valid
                  const disabled   = sourceSwitching || (isSchwab && !schwabOk)
                  const activeColor = isSchwab ? '#10b981' : '#22d3ee'
                  return (
                    <button
                      key={src}
                      onClick={() => handleSourceSwitch(src)}
                      disabled={disabled}
                      title={isSchwab && !schwabOk ? 'Schwab not authorized — visit Settings' : undefined}
                      className="flex-1 rounded-lg py-1.5 text-xs font-semibold transition-all"
                      style={{
                        fontFamily:    'JetBrains Mono, monospace',
                        fontSize:      '0.62rem',
                        letterSpacing: '0.08em',
                        background:    isActive ? `${activeColor}18` : 'transparent',
                        color:         isActive ? activeColor : '#475569',
                        border:        isActive
                          ? `1px solid ${activeColor}44`
                          : '1px solid #1e293b',
                        opacity:       disabled && !isActive ? 0.35 : 1,
                        cursor:        disabled ? 'not-allowed' : 'pointer',
                        boxShadow:     isActive ? `0 0 10px ${activeColor}22` : 'none',
                      }}
                    >
                      {sourceSwitching && !isActive ? '···' : src.toUpperCase()}
                    </button>
                  )
                })}
              </div>
              {activeSource && (
                <p className="mt-1.5 text-slate-700"
                  style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.58rem' }}>
                  {activeSource === 'schwab'
                    ? 'WebSocket streaming · sub-second ticks · all symbols'
                    : 'REST polling · rotating groups · 1 min per batch'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
